import React, { useState, useRef, useEffect, useCallback } from 'react';
import { HiChevronDown, HiX, HiCheck, HiFilter } from 'react-icons/hi';
import { createPortal } from 'react-dom';

export interface SelectOption {
  value: string;
  label: string;
}

interface FilterSelectProps {
  options: SelectOption[];
  value: SelectOption[];
  onChange: (selectedOptions: SelectOption[]) => void;
  placeholder?: string;
  className?: string;
  isMulti?: boolean;
  disabled?: boolean;
  'aria-label'?: string;
  minWidth?: string;
  padding?: string;
}

const FilterSelect: React.FC<FilterSelectProps> = ({
  options = [],
  value = [],
  onChange,
  placeholder = "Select options...",
  className = "",
  isMulti = true,
  disabled = false,
  'aria-label': ariaLabel,
  minWidth = "",
  padding = "py-2",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [visibleChips, setVisibleChips] = useState(3);
  const [dropdownPosition, setDropdownPosition] = useState<{ top: number; left: number; width: number } | null>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const dropdownContentRef = useRef<HTMLDivElement>(null);
  const chipsContainerRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Input validation
  if (!Array.isArray(options)) {
    console.error('FilterSelect: options must be an array');
    return null;
  }

  if (!Array.isArray(value)) {
    console.error('FilterSelect: value must be an array');
    return null;
  }

  if (typeof onChange !== 'function') {
    console.error('FilterSelect: onChange must be a function');
    return null;
  }

  const updateDropdownPosition = useCallback(() => {
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + 4,
        left: rect.left,
        width: rect.width,
      });
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      updateDropdownPosition();
      window.addEventListener('scroll', updateDropdownPosition, true);
      window.addEventListener('resize', updateDropdownPosition);
    } else {
      setDropdownPosition(null);
    }
    return () => {
      window.removeEventListener('scroll', updateDropdownPosition, true);
      window.removeEventListener('resize', updateDropdownPosition);
    };
  }, [isOpen, updateDropdownPosition]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const isInsideTrigger = triggerRef.current?.contains(target);
      const isInsideDropdown = dropdownContentRef.current?.contains(target);
      if (!isInsideTrigger && !isInsideDropdown) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  // Calculate visible chips with proper error handling
  const calculateVisibleChips = useCallback(() => {
    if (!chipsContainerRef.current || value.length === 0) return;

    try {
      const container = chipsContainerRef.current;
      const containerWidth = container.offsetWidth;

      if (containerWidth === 0) return; // Container not rendered yet

      // Create temporary elements to measure chip widths
      const tempChips = value.map(item => {
        if (!item?.label) return 0;

        const temp = document.createElement('span');
        temp.className = 'inline-flex items-center px-1.5 py-0.5 text-xs font-medium bg-gray-600 text-white rounded';
        temp.style.visibility = 'hidden';
        temp.style.position = 'absolute';
        temp.textContent = item.label.slice(0, 3).toUpperCase();
        document.body.appendChild(temp);
        const width = temp.offsetWidth + 4; // Add gap
        document.body.removeChild(temp);
        return width;
      });

      // Calculate +X chip width
      const tempPlusChip = document.createElement('span');
      tempPlusChip.className = 'inline-flex items-center px-1.5 py-0.5 text-xs font-medium bg-gray-500 text-white rounded';
      tempPlusChip.style.visibility = 'hidden';
      tempPlusChip.style.position = 'absolute';
      tempPlusChip.textContent = `+${value.length}`;
      document.body.appendChild(tempPlusChip);
      const plusChipWidth = tempPlusChip.offsetWidth + 4;
      document.body.removeChild(tempPlusChip);

      let totalWidth = 0;
      let fitsCount = 0;

      for (let i = 0; i < tempChips.length; i++) {
        const widthWithThisChip = totalWidth + tempChips[i];
        const needsPlusChip = i < tempChips.length - 1;
        const totalNeededWidth = widthWithThisChip + (needsPlusChip ? plusChipWidth : 0);

        if (totalNeededWidth <= containerWidth - 20) { // 20px buffer
          totalWidth = widthWithThisChip;
          fitsCount = i + 1;
        } else {
          break;
        }
      }

      setVisibleChips(Math.max(1, fitsCount));
    } catch (error) {
      console.error('FilterSelect: Error calculating visible chips:', error);
      setVisibleChips(1); // Fallback to showing 1 chip
    }
  }, [value]);

  // Recalculate chips when value changes
  useEffect(() => {
    const timer = setTimeout(calculateVisibleChips, 100);

    const handleResize = () => calculateVisibleChips();
    window.addEventListener('resize', handleResize);

    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', handleResize);
    };
  }, [calculateVisibleChips]);

  // Filter options with null safety
  const filteredOptions = options.filter(option => {
    if (!option?.label || !option?.value) return false;
    return option.label.toLowerCase().includes(searchTerm.toLowerCase());
  });

  const handleOptionToggle = useCallback((option: SelectOption) => {
    if (!option?.value) return;

    if (!isMulti) {
      onChange([option]);
      setIsOpen(false);
      return;
    }

    const isSelected = value.some(item => item?.value === option.value);
    const newValue = isSelected
      ? value.filter(item => item?.value !== option.value)
      : [...value, option];

    onChange(newValue);
  }, [value, onChange, isMulti]);

  const handleClearAll = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onChange([]);
  }, [onChange]);

  const handleSelectAll = useCallback(() => {
    onChange(filteredOptions);
  }, [filteredOptions, onChange]);

  const handleToggleDropdown = useCallback(() => {
    if (!disabled) {
      setIsOpen(!isOpen);
    }
  }, [isOpen, disabled]);

  const getButtonContent = () => {
    if (value.length === 0) {
      return (
        <div className="flex items-center gap-2">
          <HiFilter className="w-4 h-4 text-gray-400 dark:text-gray-500" />
          <span className="text-gray-500 dark:text-gray-400">{placeholder}</span>
        </div>
      );
    }

    if (value.length === 1 && value[0]?.label) {
      return (
        <div className="flex items-center gap-2 min-w-0">
          <HiFilter className="w-4 h-4 text-gray-600 dark:text-gray-400 flex-shrink-0" />
          <span className="text-gray-900 dark:text-white font-medium truncate max-w-[250px]" title={value[0].label}>
            {value[0].label}
          </span>
        </div>
      );
    }

    return (
      <div className="flex items-center gap-2 min-w-0">
        <HiFilter className="w-4 h-4 text-gray-600 dark:text-gray-400 flex-shrink-0" />
        <div ref={chipsContainerRef} className="flex gap-1 min-w-0 flex-1">
          {value.slice(0, visibleChips).map((item) => (
            item?.label && (
              <span
                key={item.value}
                className="inline-flex items-center px-1.5 py-0.5 text-xs font-medium 
                           bg-gray-600 dark:bg-gray-500 text-white rounded flex-shrink-0"
                title={item.label}
              >
                {item.label.slice(0, 3).toUpperCase()}
              </span>
            )
          ))}
          {value.length > visibleChips && (
            <span className="inline-flex items-center px-1.5 py-0.5 text-xs font-medium 
                             bg-gray-500 dark:bg-gray-600 text-white rounded flex-shrink-0">
              +{value.length - visibleChips}
            </span>
          )}
        </div>
      </div>
    );
  };

  return (
    <div ref={triggerRef} className={`relative ${className}`}>
      <button
        type="button"
        disabled={disabled}
        aria-label={ariaLabel || placeholder}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        className={`inline-flex items-center justify-between w-full ${minWidth} px-3 ${padding} text-sm
                   bg-white dark:bg-secondary-dark border rounded-lg
                   ${isOpen 
                     ? 'border-blue-500 ring-2 ring-blue-500/30' 
                     : 'border-gray-300 dark:border-gray-600 hover:border-blue-500 dark:hover:border-blue-500'}
                   focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500
                   transition-colors duration-200 shadow-sm
                   ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
        onClick={handleToggleDropdown}
      >
        <div className="flex-1 min-w-0">
          {getButtonContent()}
        </div>

        {value.length === 0 && (
          <span className="flex-shrink-0 mx-1.5 text-gray-300 dark:text-gray-500" aria-hidden="true">|</span>
        )}

        <div className={`flex items-center gap-1 flex-shrink-0 ${value.length === 0 ? 'ml-1' : 'ml-2'}`}>
          {value.length > 0 && !disabled && (
            <button
              type="button"
              onClick={handleClearAll}
              className="p-1 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 
                         transition-colors duration-200 rounded hover:bg-gray-100 dark:hover:bg-gray-700"
              aria-label="Clear all selections"
            >
              <HiX className="w-3 h-3" />
            </button>
          )}
          <HiChevronDown
            className={`w-4 h-4 text-gray-400 dark:text-gray-500 transition-transform duration-200 
                       ${isOpen ? 'rotate-180' : ''}`}
          />
        </div>
      </button>

      {isOpen && dropdownPosition && createPortal(
        <div
          ref={dropdownContentRef}
          style={{
            top: dropdownPosition.top,
            left: dropdownPosition.left,
            width: dropdownPosition.width,
          }}
          className="fixed z-[99999] bg-white dark:bg-secondary-dark border border-gray-200 dark:border-gray-600 
                     rounded-lg shadow-xl overflow-hidden"
        >
          {/* Search input */}
          <div className="p-3 border-b border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-tertiary-dark">
            <input
              ref={searchInputRef}
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search options..."
              className="w-full px-3 py-2 text-sm bg-white dark:bg-gray-800 
                         border border-gray-300 dark:border-gray-600 rounded
                         text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400
                         focus:outline-none focus:ring-1 focus:ring-gray-300/20 focus:border-gray-400"
            />
          </div>

          {/* Options list */}
          <div className="max-h-64 overflow-y-auto" role="listbox">
            {filteredOptions.length > 0 ? (
              <>
                {/* Quick actions */}
                {isMulti && filteredOptions.length > 1 && (
                  <div className="p-2 border-b border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-tertiary-dark">
                    <div className="flex justify-between w-full">
                      <button
                        type="button"
                        onClick={handleSelectAll}
                        className="px-2 py-1 text-xs font-medium text-gray-700 dark:text-gray-300 
                                   hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors duration-200"
                      >
                        Select All ({filteredOptions.length})
                      </button>
                      <button
                        type="button"
                        onClick={handleClearAll}
                        className="px-2 py-1 text-xs font-medium text-gray-700 dark:text-gray-300 
                                   hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors duration-200"
                      >
                        Clear
                      </button>
                    </div>
                  </div>
                )}

                {/* Options */}
                {filteredOptions.map((option) => {
                  if (!option?.value || !option?.label) return null;

                  const isSelected = value.some(item => item?.value === option.value);
                  return (
                    <button
                      key={option.value}
                      type="button"
                      role="option"
                      aria-selected={isSelected}
                      className="w-full px-3 py-2.5 text-sm text-left flex items-center gap-3
                                 transition-colors duration-200 hover:bg-gray-50 dark:hover:bg-gray-700
                                 text-gray-900 dark:text-gray-100"
                      onClick={() => handleOptionToggle(option)}
                    >
                      <div className={`w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0
                                      ${isSelected
                          ? 'bg-gray-600 border-gray-600'
                          : 'border-gray-300 dark:border-gray-500'
                        }`}>
                        {isSelected && <HiCheck className="w-3 h-3 text-white" />}
                      </div>
                      <span className="truncate flex-1">{option.label}</span>
                    </button>
                  );
                })}
              </>
            ) : (
              <div className="p-4 text-center text-sm text-gray-500 dark:text-gray-400">
                No options found for &quot;{searchTerm}&quot;
              </div>
            )}
          </div>

          {/* Footer with selection info */}
          {value.length > 0 && (
            <div className="p-2 bg-gray-50 dark:bg-tertiary-dark border-t border-gray-200 dark:border-gray-600">
              <div className="text-xs text-gray-600 dark:text-gray-400 text-center">
                {value.length} selected
              </div>
            </div>
          )}
        </div>,
        document.body
      )}
    </div>
  );
};

export default FilterSelect; 